import React, { useEffect, useMemo, useState } from "react";
import { FiArrowRight, FiBookOpen, FiSearch } from "react-icons/fi";
import { getAllBlogs } from "../../../../services/operations/blogApi";
import Footer from "../../../../common/Footer";
import BlogCard from "./BlogCard";
import "./Blogs.css";

const PAGE_SIZE = 6;

const getCategory = (blog) =>
  blog?.category?.name || blog?.category || blog?.topic || "Market Insights";

const AllBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchBlogs = async () => {
      const res = await getAllBlogs();
      setBlogs(Array.isArray(res) ? res : []);
      setLoading(false);
    };
    fetchBlogs();
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(blogs.map(getCategory))],
    [blogs]
  );

  const filteredBlogs = useMemo(() => {
    const search = query.trim().toLowerCase();
    return blogs.filter((blog) => {
      const matchesCategory =
        activeCategory === "All" || getCategory(blog) === activeCategory;
      const matchesSearch =
        !search ||
        `${blog.title || ""} ${blog.summary || ""}`
          .toLowerCase()
          .includes(search);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, blogs, query]);

  const featured = filteredBlogs[0];
  const remaining = filteredBlogs.slice(1);
  const totalPages = Math.max(1, Math.ceil(remaining.length / PAGE_SIZE));
  const visibleBlogs = remaining.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const selectCategory = (category) => {
    setActiveCategory(category);
    setPage(1);
  };

  return (
    <>
      <main className="blog-index">
        <section className="blog-hero">
          <div className="blog-shell blog-hero-inner">
            <div className="blog-eyebrow">
              <FiBookOpen /> CNT Knowledge Hub
            </div>
            <h1>Ideas that make you a <span>smarter investor.</span></h1>
            <p>
              Practical market insights, trading strategies, and clear financial
              lessons—written to help you make more confident decisions.
            </p>
            <label className="blog-search">
              <FiSearch aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="Search articles, topics, or strategies"
                aria-label="Search articles"
              />
            </label>
          </div>
        </section>

        <section className="blog-shell blog-content">
          <div className="blog-section-heading">
            <div>
              <p>LEARN. APPLY. GROW.</p>
              <h2>Latest from CNT Academy</h2>
            </div>
            <span>{filteredBlogs.length} articles</span>
          </div>

          {categories.length > 1 && (
            <div className="blog-filters" aria-label="Blog categories">
              {categories.map((category) => (
                <button
                  key={category}
                  className={activeCategory === category ? "active" : ""}
                  onClick={() => selectCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="blog-grid" aria-label="Loading articles">
              {[1, 2, 3].map((item) => (
                <div className="blog-card blog-skeleton" key={item}>
                  <div className="blog-skeleton-image" />
                  <div className="blog-skeleton-copy" />
                </div>
              ))}
            </div>
          ) : !featured ? (
            <div className="blog-empty">
              <FiSearch />
              <h3>No articles found</h3>
              <p>Try a different search term or browse all topics.</p>
              <button onClick={() => { setQuery(""); selectCategory("All"); }}>
                View all articles
              </button>
            </div>
          ) : (
            <>
              {page === 1 && !query && activeCategory === "All" && (
                <BlogCard blog={featured} featured />
              )}

              <div className="blog-grid">
                {(page === 1 && (query || activeCategory !== "All")
                  ? filteredBlogs.slice(0, PAGE_SIZE)
                  : visibleBlogs
                ).map((blog) => (
                  <BlogCard key={blog._id} blog={blog} />
                ))}
              </div>

              {totalPages > 1 && !query && activeCategory === "All" && (
                <nav className="blog-pagination" aria-label="Blog pagination">
                  <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1)
                    .slice(Math.max(0, page - 3), page + 2)
                    .map((number) => (
                      <button
                        key={number}
                        className={page === number ? "active" : ""}
                        onClick={() => setPage(number)}
                        aria-current={page === number ? "page" : undefined}
                      >
                        {number}
                      </button>
                    ))}
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiArrowRight />
                  </button>
                </nav>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AllBlogs;
